// frontend/src/context/ValidationContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from '../utils/axiosConfig';
import { useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { toast } from '../utils/toast';
import { handleApiError} from '../utils/apiErrorHandler';
import { RegistrationValidationContext } from '../pages/CompetitionRegistrationPage';

interface ValidationContextType {
  isValid: boolean;
  validationMessages: string[];
  refreshValidation: () => Promise<void>;
  hasValidationError: (errorType: string) => boolean;
}

const ValidationContext = createContext<ValidationContextType | undefined>(undefined);

export const useValidation = () => {
  const context = useContext(ValidationContext);
  if (!context) {
    throw new Error('useValidation must be used within a ValidationProvider');
  }
  return context;
};

interface ValidationProviderProps {
  children: ReactNode;
}

export const ValidationProvider: React.FC<ValidationProviderProps> = ({ children }) => {
  const [isValid, setIsValid] = useState<boolean>(true);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const { competitionId } = useParams<{ competitionId: string }>();
  const { clubId } = useAuth();

  // Access the parent registration validation context
  const registrationValidation = useContext(RegistrationValidationContext);

  const refreshValidation = async () => {
    if (!competitionId || !clubId) {
      return;
    }

    try {
      //const response = await axios.get(`/api/competition-registrations/validate/${competitionId}/${clubId}`);
      const response = await axios.get(`/api/competition-registrations/validate/${competitionId}`);
      const { isValid, messages } = response.data;
      
      setIsValid(isValid);
      setValidationMessages(messages || []);
      
      // Only show toast for validation errors to avoid cluttering the UI
      if (!isValid && messages && messages.length > 0) {
        const criticalErrors = messages.filter((msg: string) => 
          msg.includes('maximal') || 
          msg.includes('Meldeschluss') || 
          msg.includes('benötigt')
        );
        
        if (criticalErrors.length > 0) {
          toast.warning(criticalErrors[0], { autoClose: 5000 });
        }
      }

      // Also refresh the parent registration validation to update the progress bar
      registrationValidation.refreshValidation();
    } catch (error) {
      handleApiError(error, 'Fehler bei der Validierung.');
      setIsValid(false);
      setValidationMessages(['Validierung konnte nicht durchgeführt werden.']);
    }
  };

  const hasValidationError = (errorType: string): boolean => {
    return validationMessages.some(message => message.includes(errorType));
  };

  useEffect(() => {
    if (competitionId && clubId) {
      refreshValidation();
    }
  }, [competitionId, clubId]);

  return (
    <ValidationContext.Provider value={{ isValid, validationMessages, refreshValidation, hasValidationError }}>
      {children}
    </ValidationContext.Provider>
  );
};

export { ValidationContext };